import {errorState,loadingState} from '../ui/states.js';
import {smokehouseDashboardMarkup} from './dashboard-view.js';
import {smokehouseOnboardingStepMarkup} from './onboarding-view.js';
import {smokehouseManagementMarkup} from './manage-view.js';
import {createSmokerFormMarkup} from './smoker-create-view.js';
import {smokerArchiveMarkup} from './smoker-archive-view.js';
import {thermometerFormMarkup} from './thermometer-view.js';
import {woodInventoryFormMarkup} from './wood-inventory-view.js';
import {tastePreferencesMarkup} from './taste-preferences-view.js';

function requireRuntime(runtime){
  if(!runtime?.initialScreen||!runtime?.dashboard||!runtime?.onboarding){
    throw new TypeError('Smokehouse runtime is required');
  }
}

export function createSmokehousePageController({
  runtime,
  locale='pl',
  isOnline=()=>globalThis.navigator?.onLine!==false,
  woodCatalogue=[]
}={}){
  requireRuntime(runtime);

  let state=Object.freeze({kind:'loading'});
  let catalogue=Object.freeze([...(woodCatalogue||[])]);
  let favouriteKeys=Object.freeze([]);

  function setState(next){
    state=Object.freeze(next);
    return state;
  }

  async function refresh(){
    try{
      const [initial,catalogueResult,favouritesResult]=await Promise.all([
        runtime.initialScreen(),
        runtime.woodCatalogue?.load?.({locale})||Promise.resolve(null),
        runtime.woodFavouriteKeys?.()||Promise.resolve([])
      ]);
      if(catalogueResult?.items)catalogue=Object.freeze([...catalogueResult.items]);
      favouriteKeys=Object.freeze([...(favouritesResult||[])]);
      return setState(initial.kind==='onboarding'
        ?{kind:'onboarding',step:initial.step,profile:initial.profile,progress:initial.progress}
        :{kind:'dashboard',model:initial.model});
    }catch(error){
      return setState({kind:'error',code:String(error?.code||error?.message||'SMOKEHOUSE_LOAD_FAILED').slice(0,80)});
    }
  }

  async function openDashboard(){
    try{return setState({kind:'dashboard',model:await runtime.dashboard()});}
    catch(error){return setState({kind:'error',code:String(error?.code||error?.message||'SMOKEHOUSE_LOAD_FAILED').slice(0,80)});}
  }

  async function openManagement(resource){
    if(!['smokers','thermometers','wood'].includes(resource))throw new TypeError('Unsupported Smokehouse management kind');
    try{return setState({kind:'management',resource,model:await runtime.dashboard()});}
    catch(error){return setState({kind:'error',code:String(error?.code||error?.message||'SMOKEHOUSE_LOAD_FAILED').slice(0,80)});}
  }

  async function setDefaultSmoker(id){
    try{
      await runtime.setDefaultSmoker(id);
      return openManagement('smokers');
    }catch(error){
      return setState({
        kind:'error',
        code:String(error?.code||error?.message||'SMOKER_DEFAULT_FAILED').slice(0,80)
      });
    }
  }

  async function openArchiveSmoker(id,{historicalSessionCount=0}={}){
    try{
      const [plan,model]=await Promise.all([
        runtime.planSmokerArchive(id,{historicalSessionCount}),
        runtime.dashboard()
      ]);
      return setState({kind:'smoker_archive',plan,model,selectedReplacementId:null});
    }catch(error){
      return setState({
        kind:'error',
        code:String(error?.code||error?.message||'SMOKER_ARCHIVE_PLAN_FAILED').slice(0,80)
      });
    }
  }

  async function confirmArchiveSmoker({replacementDefaultId=null}={}){
    if(state.kind!=='smoker_archive'||!state.plan?.smokerId)throw new TypeError('Smoker archive state is required');
    const result=await runtime.archiveSmoker(state.plan.smokerId,{
      replacementDefaultId:replacementDefaultId||null,
      historicalSessionCount:state.plan.historicalSessionCount||0
    });
    if(result.ok)return openManagement('smokers');
    if(result.status==='replacement_required'){
      return setState({...state,selectedReplacementId:replacementDefaultId||null});
    }
    return setState({kind:'error',code:'SMOKER_ARCHIVE_FAILED'});
  }

  function openCreateSmoker(values={}){
    return setState({kind:'smoker_form',id:null,values:Object.freeze({...values}),errors:Object.freeze({})});
  }

  async function openSmoker(id){
    const values=await runtime.smokerFlow.load(id);
    return setState({kind:'smoker_form',id,values:Object.freeze({...values}),errors:Object.freeze({})});
  }

  async function openThermometer(id=null){
    const values=id?await runtime.thermometerFlow.load(id):{};
    return setState({kind:'thermometer_form',id,values:Object.freeze({...values}),errors:Object.freeze({})});
  }

  async function openWood(id=null){
    const values=id?await runtime.woodFlow.load(id):{};
    return setState({kind:'wood_form',id,values:Object.freeze({...values}),errors:Object.freeze({})});
  }

  async function openTastePreferences(){
    const profile=await runtime.offline.getProfile();
    return setState({kind:'taste_form',values:profile.tastePreferences||{}});
  }

  async function submitOnboarding(step,input={}){
    try{
      if(step==='experience')await runtime.onboarding.saveExperience(input.experienceLevel);
      else if(step==='goals')await runtime.onboarding.saveGoals(input.smokingGoals||[],{otherText:input.otherText||null});
      else if(step==='products')await runtime.onboarding.saveProducts(input.productPreferences||[],{otherText:input.otherText||null});
      else throw new TypeError('Unsupported onboarding step');
      return refresh();
    }catch(error){
      const current=await runtime.onboarding.state();
      return setState({
        kind:'onboarding',
        step,
        profile:current.profile,
        progress:current.progress,
        error:String(error?.message||'ONBOARDING_SAVE_FAILED').slice(0,180)
      });
    }
  }

  async function skipOnboarding(){
    await runtime.onboarding.skip();
    return openDashboard();
  }

  async function submitSmoker(input={},id=null,photoAction=null){
    const result=id
      ?await runtime.editSmoker(id,input)
      :await runtime.createSmoker(input);

    if(!result.ok){
      return setState({
        kind:'smoker_form',
        id,
        values:Object.freeze({...input}),
        errors:result.errors||Object.freeze({smoker:result.errorCode||'Nie udało się zapisać wędzarni.'}),
        result
      });
    }

    const smokerId=id||result.smokerId;
    if(photoAction&&smokerId){
      try{
        let photoResult=null;
        if(photoAction.action==='replace'){
          photoResult=await runtime.replaceSmokerPhoto(smokerId,{
            file:photoAction.file,
            mimeType:photoAction.mimeType,
            sizeBytes:photoAction.sizeBytes
          });
        }else if(photoAction.action==='remove'){
          photoResult=await runtime.removeSmokerPhoto(smokerId);
        }

        if(photoResult&&photoResult.ok===false){
          const values=await runtime.smokerFlow.load(smokerId);
          return setState({
            kind:'smoker_form',
            id:smokerId,
            values:Object.freeze({...values}),
            errors:Object.freeze({
              photo:photoResult.status==='offline'
                ?'Zdjęcie nie zostało zmienione. Połącz się z internetem i spróbuj ponownie.'
                :'Wędzarnia została zapisana, ale operacja na zdjęciu nie powiodła się.'
            }),
            result:photoResult
          });
        }
      }catch(error){
        const values=await runtime.smokerFlow.load(smokerId);
        return setState({
          kind:'smoker_form',
          id:smokerId,
          values:Object.freeze({...values}),
          errors:Object.freeze({
            photo:String(error?.message||'Nie udało się zapisać zdjęcia.').slice(0,180)
          }),
          result
        });
      }
    }

    return refresh();
  }

  async function submitThermometer(input={},id=null){
    const result=await runtime.saveThermometer(input,id?{id}:{});
    if(result.ok)return openDashboard();
    return setState({
      kind:'thermometer_form',
      id,
      values:Object.freeze({...input}),
      errors:result.errors||Object.freeze({thermometer:result.errorCode||'Nie udało się zapisać termometru.'}),
      result
    });
  }

  async function submitWood(input={},id=null,favourite=false){
    const result=await runtime.saveWood(input,id?{id}:{});
    if(result.ok){
      if(input.catalogueKey&&typeof runtime.setWoodFavourite==='function'){
        await runtime.setWoodFavourite(input.catalogueKey,Boolean(favourite));
        favouriteKeys=Object.freeze([...(await runtime.woodFavouriteKeys())]);
      }
      return openDashboard();
    }
    return setState({
      kind:'wood_form',
      id,
      values:Object.freeze({...input}),
      errors:result.errors||Object.freeze({wood:result.errorCode||'Nie udało się zapisać drewna.'}),
      result
    });
  }

  async function submitTastePreferences(values={}){
    try{
      await runtime.saveTastePreferences(values);
      return openDashboard();
    }catch(error){
      return setState({
        kind:'taste_form',
        values:Object.freeze({...values}),
        error:String(error?.message||'TASTE_PREFERENCES_SAVE_FAILED').slice(0,180)
      });
    }
  }

  function setWoodCatalogue(items=[]){
    catalogue=Object.freeze([...(items||[])]);
    return catalogue;
  }

  function render(){
    switch(state.kind){
      case 'loading':
        return loadingState({title:'Ładowanie Mojej wędzarni',text:'Odtwarzamy lokalne dane i stan synchronizacji.'});
      case 'error':
        return errorState({title:'Nie udało się otworzyć Mojej wędzarni',text:'Dane lokalne pozostają bezpieczne. Spróbuj ponownie.',retryId:'smokehouse-retry'});
      case 'dashboard':
        return smokehouseDashboardMarkup(state.model);
      case 'management':
        return smokehouseManagementMarkup(state.resource,state.model);
      case 'onboarding':
        return smokehouseOnboardingStepMarkup({step:state.step,profile:state.profile,locale});
      case 'smoker_form':
        return createSmokerFormMarkup({values:state.values,errors:state.errors,locale,offline:!isOnline(),editing:Boolean(state.id)});
      case 'smoker_archive':
        return smokerArchiveMarkup({
          plan:state.plan,
          model:state.model,
          selectedReplacementId:state.selectedReplacementId
        });
      case 'thermometer_form':
        return thermometerFormMarkup({values:state.values,errors:state.errors,locale,offline:!isOnline(),editing:Boolean(state.id)});
      case 'wood_form':
        return woodInventoryFormMarkup({
          values:state.values,
          errors:state.errors,
          catalogue,
          favouriteKeys,
          offline:!isOnline(),
          editing:Boolean(state.id)
        });
      case 'taste_form':
        return tastePreferencesMarkup(state.values,{locale});
      default:
        return errorState({title:'Nieznany stan Mojej wędzarni',text:'Odśwież widok.'});
    }
  }

  return Object.freeze({
    refresh,
    openDashboard,
    openManagement,
    openCreateSmoker,
    openSmoker,
    openThermometer,
    openWood,
    openTastePreferences,
    submitOnboarding,
    skipOnboarding,
    submitSmoker,
    setDefaultSmoker,
    openArchiveSmoker,
    confirmArchiveSmoker,
    submitThermometer,
    submitWood,
    submitTastePreferences,
    setWoodCatalogue,
    render,
    getState:()=>state
  });
}
